import { logger } from '@/utils/logger/logger';
import { transcribeDictationChunk } from '@heidi/dictate/data';
import { isEditorLive, whenEditorLive } from '@heidi/editor/core';
import { sanitizeError } from '@heidi/runtime/logger';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { type Editor } from '@tiptap/react';
import pRetry from 'p-retry';

import {
  type DiscardedChunk,
  PCM_SAMPLE_RATE,
  PcmChunker,
} from './dictateRecorderPcmChunker';
import {
  discardMediaStream,
  isDictationStartCancelled,
  PCM_WORKLET_MODULE_URL,
  takeLiveAcquiredStream,
} from './dictateRecorderShared';
import { type DictationRecorderStartArgs } from './dictateRecorderTransport';

// A returned chunk is revealed word by word (a few characters at a time for
// scripts written without spaces) so text arriving in 5-10s batches still
// reads as live dictation.
const REVEAL_BUDGET_MS = 1200;
const REVEAL_MAX_STEP_MS = 60;
const REVEAL_CHARS_PER_STEP = 4;

// After stop the user is waiting for the final text (smart mode shows a
// spinner, word-for-word an idle mic), so a stalled backend gets this long
// before the outstanding chunks are abandoned — far less than the per-request
// timeout times its retries.
export const FINISH_DEADLINE_MS = 20_000;

const WAV_HEADER_BYTES = 44;
const WAV_BYTES_PER_MS = (PCM_SAMPLE_RATE * 2) / 1000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const revealSteps = (text: string) => {
  const words = text.match(/\S+\s*/g) ?? [];
  if (words.length >= 3) {
    return words;
  }
  // Scripts written without spaces come back as one "word".
  const slices: string[] = [];
  for (let i = 0; i < text.length; i += REVEAL_CHARS_PER_STEP) {
    slices.push(text.slice(i, i + REVEAL_CHARS_PER_STEP));
  }
  return slices;
};

const errorName = (error: unknown) =>
  error instanceof Error ? error.name : typeof error;

// getUserMedia rejections the user can act on, by the DOMException name.
const microphoneErrorMessage = (error: unknown) => {
  switch (errorName(error)) {
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'Microphone not found. Please choose another microphone in settings';
    case 'NotReadableError':
      return 'Microphone is in use by another app. Please close it and try again';
    default:
      return 'Microphone permission denied. Please allow access to continue';
  }
};

/**
 * Dictation recorder for builds with no streaming transcriber: PCM is cut into
 * short WAV chunks, each transcribed over HTTP, and the results are written
 * into the editor in chunk order through the same commands the WebSocket
 * recorder drives.
 */
class DictationAudioChunked {
  private static instance: DictationAudioChunked | null = null;
  private mediaStream: MediaStream | null = null;
  public isRecording: boolean = false;
  private isStarting: boolean = false;
  // Bumped by stop()/stopImmediate() so an in-flight onStart can tell it was
  // cancelled during getUserMedia (or any later await) and drop the new tracks.
  private startGeneration: number = 0;
  // Identifies one start→finalize run; render and finalize work belonging to a
  // replaced run must not touch the next run's editor.
  private runId: number = 0;
  private isFinishing: boolean = false;
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private editor: Editor | null = null;
  private sessionId: string | null = null;
  private chunker = this.createChunker();
  private abortController: AbortController | null = null;
  private onError: ((errorMessage?: string) => void) | null = null;
  private nextChunkIndex = 0;
  private nextRenderIndex = 0;
  private results = new Map<number, string>();
  private inFlight = new Set<Promise<void>>();
  private renderChain: Promise<void> = Promise.resolve();
  private constructor() {}

  static getInstance() {
    if (!this.instance) {
      this.instance = new DictationAudioChunked();
    }
    return this.instance;
  }

  isBoundTo(editor: Editor) {
    return this.editor === editor;
  }

  async onStart({ editor, deviceId, onError, onSuccess }: DictationRecorderStartArgs) {
    if (this.isStarting) {
      logger.debug('Dictation start ignored: a start is already in progress');
      return;
    }
    if (this.editor) {
      this.stopImmediate();
    }
    const sessionId = getCurrentSessionId();
    if (!sessionId) {
      onError?.('Dictation is only available inside a session');
      return;
    }
    this.isStarting = true;
    const currentGeneration = ++this.startGeneration;
    try {
      this.resetRun();
      this.editor = editor;
      this.sessionId = sessionId;
      this.onError = onError ?? null;
      this.abortController = new AbortController();

      let acquiredStream: MediaStream;
      try {
        // eslint-disable-next-line @heidi/rules/no-unguarded-browser-media -- inline note dictation captures through the WebView on every platform, same as the WebSocket recorder; there is no native capture path for it
        acquiredStream = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: {
            deviceId: deviceId || 'default',
            autoGainControl: false,
            echoCancellation: false,
            noiseSuppression: false,
          },
        });
      } catch (error) {
        onError?.(microphoneErrorMessage(error));
        logger.error({
          event_type: 'DICTATION_GET_AUDIO_STREAM_ERROR',
          details: { errorName: errorName(error), ...sanitizeError(error) },
        });
        this.editor = null;
        return;
      }
      const keptStream = takeLiveAcquiredStream(
        acquiredStream,
        currentGeneration,
        this.startGeneration,
        this.editor,
        editor
      );
      if (!keptStream) {
        return;
      }
      this.mediaStream = keptStream;

      this.audioContext = new AudioContext({ sampleRate: PCM_SAMPLE_RATE });
      if (this.audioContext.state === 'suspended') {
        try {
          await this.audioContext.resume();
        } catch (error) {
          // A stop during the await has already torn the context down; that
          // rejection is the cancellation, not a failure to report.
          if (this.isStartCancelled(currentGeneration, editor)) {
            this.stopAudioProcessing();
            return;
          }
          logger.error({
            event_type: 'DICTATION_RESUME_AUDIO_CONTEXT_ERROR',
            details: { errorName: errorName(error) },
          });
          onError?.('Failed to initialize audio context');
          this.stopAudioProcessing();
          return;
        }
        if (this.isStartCancelled(currentGeneration, editor)) {
          this.stopAudioProcessing();
          return;
        }
      }
      this.source = this.audioContext.createMediaStreamSource(this.mediaStream);
      await this.audioContext.audioWorklet.addModule(PCM_WORKLET_MODULE_URL);
      if (this.isStartCancelled(currentGeneration, editor)) {
        this.stopAudioProcessing();
        return;
      }

      this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-processor');
      this.workletNode.port.onmessage = (event) => {
        const chunk = this.chunker.push(event.data as ArrayBuffer);
        if (chunk) {
          this.submitChunk(chunk);
        }
      };
      this.source.connect(this.workletNode).connect(this.audioContext.destination);

      this.isRecording = true;
      onSuccess?.();
    } catch (error) {
      onError?.(typeof error === 'string' ? error : 'Could not start dictation');
      this.stopAudioProcessing();
      this.editor = null;
      logger.error({
        event_type: 'DICTATION_START_ERROR',
        details: { errorName: errorName(error), ...sanitizeError(error) },
      });
    } finally {
      this.isStarting = false;
    }
  }

  stop() {
    this.startGeneration += 1;
    this.isRecording = false;
    const tail = this.chunker.flush();
    this.stopAudioProcessing();
    if (!this.editor || this.isFinishing) {
      return;
    }
    this.isFinishing = true;
    if (tail) {
      this.submitChunk(tail);
    }
    void this.finishRun(this.runId);
  }

  private isStartCancelled(currentGeneration: number, editor: Editor) {
    return isDictationStartCancelled(
      currentGeneration,
      this.startGeneration,
      this.editor,
      editor
    );
  }

  private createChunker() {
    return new PcmChunker((discarded: DiscardedChunk) =>
      logger.warn({
        event_type: 'DICTATION_CHUNK_DISCARDED_AS_SILENT',
        details: discarded,
      })
    );
  }

  // Waits for the chunks still in flight so the last words land before the
  // extension finalizes (and, in smart mode, sends the transcript for cleanup).
  private async finishRun(runId: number) {
    let deadlineId: ReturnType<typeof setTimeout> | undefined;
    const timedOut = await Promise.race([
      Promise.allSettled([...this.inFlight]).then(() => false),
      new Promise<boolean>((resolve) => {
        deadlineId = setTimeout(() => resolve(true), FINISH_DEADLINE_MS);
      }),
    ]);
    clearTimeout(deadlineId);
    if (runId !== this.runId) {
      return;
    }
    if (timedOut) {
      // The transcript ends at the last contiguous chunk. Anything that
      // answered behind a stalled chunk is dropped too, because writing it
      // would join text across missing speech.
      logger.error({
        event_type: 'DICTATION_CHUNK_FINISH_TIMEOUT',
        details: {
          pendingChunks: this.inFlight.size,
          answeredUnrenderedChunks: this.results.size,
        },
      });
      this.abortController?.abort();
      this.reportError('Failed to generate transcript');
    }
    await this.renderChain;
    if (runId !== this.runId) {
      return;
    }
    const editor = this.editor;
    this.resetRun();
    if (editor) {
      whenEditorLive(editor, 'dictateRecorderChunked.finishRun', (liveEditor) =>
        liveEditor.commands.hideDictationCursor()
      );
      editor.commands.finalizeDictation();
    }
  }

  // Synchronous teardown of the current run, used when a new start replaces it.
  // The replaced run's tail and in-flight chunks are abandoned, because its
  // editor is finalized here and now; the loss is logged so it is visible.
  private stopImmediate() {
    this.startGeneration += 1;
    this.isRecording = false;
    const tail = this.chunker.flush();
    this.stopAudioProcessing();
    if (tail || this.inFlight.size > 0) {
      logger.warn({
        event_type: 'DICTATION_RUN_REPLACED',
        details: {
          discardedTailMs: tail
            ? Math.max(0, Math.round((tail.size - WAV_HEADER_BYTES) / WAV_BYTES_PER_MS))
            : 0,
          pendingChunks: this.inFlight.size,
        },
      });
    }
    const editor = this.editor;
    this.resetRun();
    editor?.commands.finalizeDictation();
  }

  private resetRun() {
    this.runId += 1;
    this.abortController?.abort();
    this.abortController = null;
    this.editor = null;
    this.sessionId = null;
    this.onError = null;
    this.isFinishing = false;
    this.chunker = this.createChunker();
    this.nextChunkIndex = 0;
    this.nextRenderIndex = 0;
    this.results = new Map();
    this.inFlight = new Set();
    this.renderChain = Promise.resolve();
  }

  // Dropping a chunk silently would lose speech the clinician believes was
  // captured, so a chunk that comes back unusable ends the run at that chunk:
  // nothing after it is written, so the text never reads as continuous across
  // the missing speech.
  private failRun(abortController: AbortController) {
    abortController.abort();
    this.reportError('Failed to generate transcript');
    this.stop();
  }

  private reportError(message: string) {
    const onError = this.onError;
    this.onError = null;
    onError?.(message);
  }

  private submitChunk(audio: Blob) {
    const { sessionId, abortController, runId } = this;
    if (!sessionId || !abortController || abortController.signal.aborted) {
      return;
    }
    const index = this.nextChunkIndex++;
    const { signal } = abortController;
    const request = pRetry(() => transcribeDictationChunk({ sessionId, audio, signal }), {
      retries: 2,
      minTimeout: 300,
      maxTimeout: 1000,
      signal,
    })
      .then((transcript) => {
        if (runId !== this.runId) {
          return;
        }
        // The chunker only sends audio it classified as speech, so a blank
        // answer for it is a transcriber fault, not a legitimate silence.
        if (!transcript.trim()) {
          logger.error({
            event_type: 'DICTATION_CHUNK_BLANK_TRANSCRIPT',
            details: { chunkIndex: index, audioBytes: audio.size },
          });
          this.failRun(abortController);
          return;
        }
        this.results.set(index, transcript);
        this.drainResults();
      })
      .catch((error: unknown) => {
        if (runId !== this.runId || signal.aborted) {
          return;
        }
        logger.error({
          event_type: 'DICTATION_CHUNK_TRANSCRIBE_ERROR',
          details: { errorName: errorName(error), chunkIndex: index },
        });
        this.failRun(abortController);
      })
      .finally(() => {
        this.inFlight.delete(request);
      });
    this.inFlight.add(request);
  }

  // Chunks are transcribed concurrently and may answer out of order; they are
  // written strictly by index. A chunk that never answers holds everything
  // after it back.
  private drainResults() {
    while (this.results.has(this.nextRenderIndex)) {
      const text = (this.results.get(this.nextRenderIndex) ?? '').trim();
      this.results.delete(this.nextRenderIndex);
      this.nextRenderIndex += 1;
      const runId = this.runId;
      this.renderChain = this.renderChain.then(() => this.renderChunk(text, runId));
    }
  }

  private async renderChunk(text: string, runId: number) {
    const editor = this.editor;
    if (!editor || runId !== this.runId) {
      return;
    }
    // Once stopped the user is waiting on the final text, not watching it type.
    if (!this.isFinishing) {
      const steps = revealSteps(text);
      const stepMs = Math.min(REVEAL_MAX_STEP_MS, REVEAL_BUDGET_MS / steps.length);
      let revealed = '';
      for (const step of steps.slice(0, -1)) {
        revealed += step;
        editor.commands.setPartialTranscript(revealed);
        await sleep(stepMs);
        if (!isEditorLive(editor, 'dictateRecorderChunked.renderChunk')) {
          return;
        }
        if (runId !== this.runId || this.isFinishing) {
          break;
        }
      }
    }
    if (runId !== this.runId) {
      return;
    }
    editor.commands.setPartialTranscript(text);
    editor.commands.setConfirmedTranscript(text);
    editor.commands.setFullTranscript(`${text} `);
  }

  private stopAudioProcessing() {
    discardMediaStream(this.mediaStream);
    try {
      if (this.workletNode) {
        this.workletNode.port.onmessage = null;
        this.workletNode.disconnect();
        this.workletNode.port.close();
      }
      this.source?.disconnect();
    } catch (err) {
      logger.debug('Error disconnecting audio nodes', { errorName: errorName(err) });
    }
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext
        .close()
        .catch((err: unknown) =>
          logger.debug('Error closing audio context', { errorName: errorName(err) })
        );
    }
    this.mediaStream = null;
    this.audioContext = null;
    this.workletNode = null;
    this.source = null;
  }
}

export default DictationAudioChunked;
